import { useState } from "react";
import axios from "axios";
import { Download, CheckCircle2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PDF_PATH = "/employee-benefits-north-dakota.pdf";

const triggerDownload = () => {
  const a = document.createElement("a");
  a.href = PDF_PATH;
  a.download = "employee-benefits-north-dakota.pdf";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

export const GuideDownloadDialog = ({ open, onOpenChange }) => {
  const [form, setForm] = useState({ name: "", email: "", business_name: "" });
  const [status, setStatus] = useState("idle");

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/guide-lead`, form);
    } catch (err) {
      // still deliver the guide even if the lead capture call fails
    }
    setStatus("success");
    triggerDownload();
  };

  const handleOpenChange = (isOpen) => {
    onOpenChange(isOpen);
    if (!isOpen) {
      setStatus("idle");
      setForm({ name: "", email: "", business_name: "" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="guide-download-dialog" className="max-w-md rounded-2xl">
        {status === "success" ? (
          <div className="text-center py-6" data-testid="guide-download-success">
            <div className="w-14 h-14 bg-[#EAF9F9] rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 size={28} className="text-[#2EC4C4]" />
            </div>
            <h3 className="font-bold text-xl text-[#1A2744] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Your Guide Is Downloading!
            </h3>
            <p className="text-[#475569] text-sm leading-relaxed mb-4">
              If the download didn't start, use the button below. We'll follow up with helpful benefits tips for North Dakota employers.
            </p>
            <button
              type="button"
              onClick={triggerDownload}
              data-testid="guide-download-again-btn"
              className="inline-flex items-center space-x-2 bg-[#2EC4C4] hover:bg-[#25A0A0] text-white font-semibold rounded-lg px-6 py-3 transition-all"
            >
              <Download size={18} />
              <span>Download Again</span>
            </button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl text-[#1A2744]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Get Your Free Benefits Guide
              </DialogTitle>
              <DialogDescription className="text-[#475569] text-sm">
                Tell us where to send benefits insights and your download will start right away.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-2" data-testid="guide-lead-form">
              <div>
                <label className="block text-sm font-semibold text-[#1A2744] mb-1.5">Name *</label>
                <input
                  type="text"
                  name="name"
                  required
                  value={form.name}
                  onChange={handleChange}
                  data-testid="guide-lead-name-input"
                  placeholder="Your name"
                  className="w-full rounded-lg border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2EC4C4] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1A2744] mb-1.5">Email *</label>
                <input
                  type="email"
                  name="email"
                  required
                  value={form.email}
                  onChange={handleChange}
                  data-testid="guide-lead-email-input"
                  placeholder="you@company.com"
                  className="w-full rounded-lg border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2EC4C4] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1A2744] mb-1.5">Business Name</label>
                <input
                  type="text"
                  name="business_name"
                  value={form.business_name}
                  onChange={handleChange}
                  data-testid="guide-lead-business-input"
                  placeholder="Optional"
                  className="w-full rounded-lg border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2EC4C4] focus:border-transparent"
                />
              </div>
              <button
                type="submit"
                disabled={status === "sending"}
                data-testid="guide-lead-submit-btn"
                className="w-full inline-flex items-center justify-center space-x-2 bg-[#2EC4C4] hover:bg-[#25A0A0] disabled:opacity-60 text-white font-semibold rounded-lg px-6 py-3 transition-all shadow-md"
              >
                {status === "sending" ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
                <span>{status === "sending" ? "Sending..." : "Download the Guide"}</span>
              </button>
              <p className="text-[#94A3B8] text-xs text-center">
                We respect your inbox. No spam, ever.
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
